import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import '../pages/Settings.scss';
import './EntraMigrationSettings.scss';

// "מעבר לכניסה החדשה": the migration status for super admins
// (docs/EntraFullMigrationPlan.md §3.6, task 1.8). Reads GET
// /api/entra-migration/status: every user, where they stand in the move and
// their last activity. A stuck user is handled by the team, case by case.

type Department = { hospital: string; department: string; role: string };
type Person = {
  _id: string;
  name: string;
  email: string;
  lastActivity: string | null;
  dateAdded: string | null;
  departments: Department[];
  stage: 'legacy' | 'moved' | 'microsoft';
  signInMethods: { idp: string | null }[];
  prompt?: string;
  admins?: { name: string; email: string }[];
  movedAt?: string;
};
type Refused = Person & { reason: string; at: string | null };
type Status = {
  migration: string;
  counts: {
    users: number;
    legacy: number;
    moved: number;
    microsoft: number;
    movedBy: Record<string, number>;
    prompt: Record<string, number>;
  };
  movedPerDay: { day: string; count: number }[];
  users: Person[];
  refused: Refused[];
  newAccounts: Person[];
};
type StageFilter = 'all' | 'legacy' | 'moved' | 'microsoft';
type HospitalOption = { _id: string; nameHebrew: string; departments?: { _id: string; nameHebrew: string }[] };

const MIGRATION_LABELS: Record<string, string> = {
  off: 'לפני המעבר (רק משתמשי הבדיקה)',
  cutoff: 'המעבר פעיל: הכניסה הישנה סגורה',
  closed: 'נסגר: גם חיבורים ישנים נותקו',
};
const PROMPT_LABELS: Record<string, string> = {
  not_shown: 'עוד לא ראו את ההודעה',
  later_0: 'ראו, בלי "לא עכשיו"',
  later_1: '"לא עכשיו" פעם אחת',
  later_2: '"לא עכשיו" פעמיים',
  blocking: 'במסך החוסם',
};
const IDP_LABELS: Record<string, string> = { local: 'מייל וסיסמה', 'google.com': 'Google' };
const STAGE_LABELS: Record<string, string> = {
  legacy: 'כניסה ישנה',
  moved: 'עבר',
  microsoft: 'Microsoft מההתחלה',
};
const REASON_LABELS: Record<string, string> = {
  email_mismatch: 'נרשם במייל אחר',
  identity_in_use: 'חשבון Microsoft כבר מקושר למשתמש אחר',
  super_admin: 'מנהל על (מקושר רק ידנית)',
  inactive: 'החשבון אינו פעיל',
  unverified_email: 'מייל לא מאומת',
  migration_off: 'המעבר עוד לא פתוח עבורו',
  conflict: 'התנגשות, לנסות שוב',
};
const DEFAULT_ERROR = 'שגיאה בטעינת סטטוס המעבר. נסו שוב.';

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-GB');
};
const daysAgo = (days: number): string => {
  const date = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return date.toISOString().slice(0, 10);
};
const departmentsText = (person: Person): string => (person.departments.length
  ? person.departments.map(d => `${d.department || '—'} · ${d.hospital || '—'}`).join(', ')
  : 'ללא מחלקה');

const EntraMigrationSettings: React.FC = () => {
  const { user, hospitals } = useContext(UserContext) as { user: { isSuperAdmin?: boolean } | null; hospitals: HospitalOption[] };
  const isSuperAdmin = Boolean(user?.isSuperAdmin);
  const [status, setStatus] = useState<Status | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hospital, setHospital] = useState('');
  const [department, setDepartment] = useState('');
  const [newSince, setNewSince] = useState(daysAgo(30));
  const [stageFilter, setStageFilter] = useState<StageFilter>('legacy');
  const [search, setSearch] = useState('');

  const departmentOptions = useMemo(
    () => (hospitals || []).find(h => h._id === hospital)?.departments || [],
    [hospitals, hospital],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await API.get<Status>('/api/entra-migration/status', {
        params: {
          hospital: hospital || undefined,
          department: department || undefined,
          newSince: newSince || undefined,
        },
        handlingErrorLocally: true,
      });
      setStatus(response.data);
    } catch (err: unknown) {
      console.error('Loading the migration status failed:', err);
      const apiError = err as { response?: { data?: { error?: { hebMessage?: string } } } };
      setError(apiError.response?.data?.error?.hebMessage || DEFAULT_ERROR);
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [hospital, department, newSince]);

  useEffect(() => {
    if (isSuperAdmin) load();
  }, [isSuperAdmin, load]);

  const shownUsers = useMemo(() => {
    const text = search.trim().toLowerCase();
    return (status?.users || []).filter(person => (stageFilter === 'all' || person.stage === stageFilter)
      && (!text || `${person.name} ${person.email} ${departmentsText(person)}`.toLowerCase().includes(text)));
  }, [status, stageFilter, search]);

  if (!isSuperAdmin) return null;
  const counts = status?.counts;

  return (
    <div className="settings-section entra-migration-settings">
      <h2 className="section-title">מעבר לכניסה החדשה</h2>
      <p className="em-description">
        כל המשתמשים (חוץ מחשבונות מושעים ובארכיון). &quot;פעילות אחרונה&quot; היא הכניסה או השימוש האחרון
        באפליקציה ב-95 הימים האחרונים. משתמש שנתקע מטפלים בו ידנית.
      </p>

      <div className="em-toolbar">
        <select className="input-field" value={hospital} onChange={(e) => { setHospital(e.target.value); setDepartment(''); }}>
          <option value="">כל בתי החולים</option>
          {(hospitals || []).map(h => <option key={h._id} value={h._id}>{h.nameHebrew}</option>)}
        </select>
        <select className="input-field" value={department} onChange={(e) => setDepartment(e.target.value)} disabled={!hospital}>
          <option value="">כל המחלקות</option>
          {departmentOptions.map(d => <option key={d._id} value={d._id}>{d.nameHebrew}</option>)}
        </select>
        <label className="em-date">
          חשבונות חדשים מתאריך
          <input type="date" className="input-field" value={newSince} onChange={(e) => setNewSince(e.target.value)} />
        </label>
        <button type="button" className="button-primary" onClick={load} disabled={isLoading}>רענון</button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {isLoading && <div className="loading">טוען...</div>}

      {status && counts && (
        <>
          <div className="em-phase">מצב: {MIGRATION_LABELS[status.migration] || status.migration}</div>

          <div className="em-cards">
            <div className="em-card"><div className="em-number">{counts.users}</div>משתמשים</div>
            <div className="em-card"><div className="em-number">{counts.moved}</div>עברו</div>
            <div className="em-card"><div className="em-number">{counts.legacy}</div>עוד בכניסה הישנה</div>
            <div className="em-card"><div className="em-number">{counts.microsoft}</div>נרשמו ישר ב-Microsoft</div>
          </div>

          <div className="em-breakdowns">
            <div>
              <h3>מי שעוד לא עבר</h3>
              <ul>
                {Object.entries(PROMPT_LABELS).map(([key, label]) => (
                  <li key={key}>{label}: <b>{counts.prompt[key] || 0}</b></li>
                ))}
              </ul>
            </div>
            <div>
              <h3>עברו, לפי דרך הכניסה</h3>
              <ul>
                {Object.keys(counts.movedBy).length === 0 && <li>עוד אף אחד</li>}
                {Object.entries(counts.movedBy).map(([idp, count]) => (
                  <li key={idp}>{IDP_LABELS[idp] || idp}: <b>{count}</b></li>
                ))}
              </ul>
            </div>
            <div>
              <h3>מעברים לפי יום</h3>
              <ul>
                {status.movedPerDay.length === 0 && <li>עוד אף אחד</li>}
                {status.movedPerDay.slice(-14).map(({ day, count }) => (
                  <li key={day}>{formatDate(day)}: <b>{count}</b></li>
                ))}
              </ul>
            </div>
          </div>

          <h3>משתמשים ({shownUsers.length})</h3>
          <div className="em-toolbar">
            <select className="input-field" value={stageFilter} onChange={(e) => setStageFilter(e.target.value as StageFilter)}>
              <option value="legacy">עוד לא עברו</option>
              <option value="moved">עברו</option>
              <option value="microsoft">Microsoft מההתחלה</option>
              <option value="all">כולם</option>
            </select>
            <input
              className="input-field"
              placeholder="חיפוש לפי שם, מייל או מחלקה"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {shownUsers.length === 0 ? <div className="no-data">אין</div> : (
            <div className="em-table-wrapper">
              <table className="hospitals-table em-table">
                <thead>
                  <tr><th>משתמש</th><th>מצב</th><th>הודעה / דרך כניסה</th><th>מחלקות</th><th>מנהלי המחלקה</th><th>פעילות אחרונה</th></tr>
                </thead>
                <tbody>
                  {shownUsers.map(person => (
                    <tr key={person._id}>
                      <td><Link to={`/users/${person._id}`}>{person.name}</Link><div className="em-email">{person.email}</div></td>
                      <td>{STAGE_LABELS[person.stage]}{person.movedAt ? ` (${formatDate(person.movedAt)})` : ''}</td>
                      <td>
                        {person.stage === 'legacy'
                          ? (PROMPT_LABELS[person.prompt || ''] || person.prompt)
                          : person.signInMethods.map(m => IDP_LABELS[m.idp || 'local'] || m.idp).join(', ')}
                      </td>
                      <td>{departmentsText(person)}</td>
                      <td>{person.admins?.length ? person.admins.map(a => a.name).join(', ') : '—'}</td>
                      <td>{formatDate(person.lastActivity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3>מעברים שנכשלו ({status.refused.length})</h3>
          {status.refused.length === 0 ? <div className="no-data">אין</div> : (
            <div className="em-table-wrapper">
              <table className="hospitals-table em-table">
                <thead><tr><th>משתמש</th><th>סיבה</th><th>מתי</th><th>מחלקות</th><th>פעילות אחרונה</th></tr></thead>
                <tbody>
                  {status.refused.map(person => (
                    <tr key={person._id}>
                      <td><Link to={`/users/${person._id}`}>{person.name}</Link><div className="em-email">{person.email}</div></td>
                      <td>{REASON_LABELS[person.reason] || person.reason}</td>
                      <td>{formatDate(person.at)}</td>
                      <td>{departmentsText(person)}</td>
                      <td>{formatDate(person.lastActivity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3>חשבונות Microsoft חדשים מ-{formatDate(newSince)} ({status.newAccounts.length})</h3>
          <p className="em-description">כאן מופיע מי שנרשם במייל אחר במקום לעבור עם החשבון הישן. מטפלים בו ידנית.</p>
          {status.newAccounts.length === 0 ? <div className="no-data">אין</div> : (
            <div className="em-table-wrapper">
              <table className="hospitals-table em-table">
                <thead><tr><th>משתמש</th><th>נוצר</th><th>דרך כניסה</th><th>מחלקות</th></tr></thead>
                <tbody>
                  {status.newAccounts.map(person => (
                    <tr key={person._id}>
                      <td><Link to={`/users/${person._id}`}>{person.name}</Link><div className="em-email">{person.email}</div></td>
                      <td>{formatDate(person.dateAdded)}</td>
                      <td>{person.signInMethods.map(m => IDP_LABELS[m.idp || 'local'] || m.idp).join(', ')}</td>
                      <td>{departmentsText(person)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EntraMigrationSettings;
